import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { FileText, Eye, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { getCountryName } from '../../utils/countries';

const PUBLIC_BASE_URL = process.env.REACT_APP_PUBLIC_BASE_URL || window.location.origin;
const buildQrUrl = (token) => `${PUBLIC_BASE_URL}/public/qr/${token}`;

const Section = ({ title, rows }) => {
  const filtered = rows.filter(([, v]) => v);
  if (filtered.length === 0) return null;
  return (
    <div className="mb-4">
      <h3 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-1 mb-2 print:text-black">{title}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
        {filtered.map(([label, value], i) => (
          <div key={i} className="flex flex-col sm:flex-row">
            <span className="text-xs text-slate-500 sm:w-40 sm:shrink-0 print:text-gray-600">{label}:</span>
            <span className="text-xs text-slate-800 print:text-black break-words overflow-wrap-anywhere">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const badge = (val) => {
  if (!val) return null;
  const isPos = ['Yes', 'Pass', 'Confirmed'].includes(val);
  const isNeg = ['No', 'Fail', 'Not confirmed'].includes(val);
  if (!isPos && !isNeg) return val;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded ${isPos ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>{val}</span>;
};

export const RoofingPrintView = ({ product, project, user, showActions, onPreviewSubmittal, onDownloadSubmittal }) => {
  if (!product) return null;
  const perf = product.performance || {};
  const fc = product.fireCompliance || {};
  const fd = product.flashingDetails || {};
  const w = product.warranty || {};
  const qrToken = product.qrToken;
  const imgUrl = product.imageCloudinary?.urls?.medium || product.imageUrl || null;

  return (
    <div className="p-4 sm:p-6 text-slate-800 bg-white">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4 pb-3 border-b">
        <div className="flex items-start gap-3">
          {imgUrl ? (
            <img src={imgUrl} alt={product.productNameModel} className="w-16 h-16 rounded-lg object-cover border border-slate-200 shrink-0" />
          ) : (
            <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><FileText className="h-6 w-6 text-slate-300" /></div>
          )}
          <div>
            <h2 className="text-base font-bold text-slate-900">{product.productNameModel}</h2>
            <p className="text-xs text-slate-500">{product.manufacturerName || ''}</p>
            {project?.name && <p className="text-xs text-slate-400 mt-0.5">Project: {project.name}</p>}
          </div>
        </div>
        {qrToken && (
          <div className="shrink-0 flex flex-col items-center">
            <QRCodeSVG value={buildQrUrl(qrToken)} size={56} level="L" />
            <span className="text-[8px] text-slate-400 mt-1">Specard QR</span>
          </div>
        )}
      </div>

      <Section title="Product Identification" rows={[
        ['Product Role', product.productRole],
        ['Roofing Type', product.roofingType],
        ['Application Area', Array.isArray(product.applicationArea) ? product.applicationArea.join(', ') : product.applicationArea],
        ['Country', product.countryCode ? getCountryName(product.countryCode) : null],
      ]} />

      <Section title="Performance & Environmental" rows={[
        ['Wind Uplift Rating', perf.windUpliftRating],
        ['Test Standards', perf.testStandards?.join(', ')],
        ['Hail Resistance', perf.hailResistance],
        ['SRI', perf.sri],
        ['Ponding Water Resistance', badge(perf.pondingWaterResistance)],
      ]} />

      <Section title="Fire & Code Compliance" rows={[
        ['Fire Rating', fc.fireRating],
        ['UL / FM Assembly No', fc.ulFmAssemblyNo],
        ['IBC Compliance Statement', fc.ibcComplianceStatement],
        ['NFPA References', fc.nfpaReferences],
      ]} />

      <Section title="Flashing & Termination Details" rows={[
        ['Flashing Type', fd.flashingType],
        ['Termination Height', fd.terminationHeight],
        ['Sealant / Accessory Compatibility', fd.sealantCompatibility],
        ['Interfaces with', Array.isArray(fd.interfaceWith) ? fd.interfaceWith.join(', ') : fd.interfaceWith],
      ]} />

      <Section title="Warranty & Responsibility" rows={[
        ['Manufacturer Warranty', w.manufacturerWarrantyYears ? `${w.manufacturerWarrantyYears} years` : null],
        ['Installer Warranty', w.installerWarrantyYears ? `${w.installerWarrantyYears} years` : null],
        ['Single-Source Warranty', badge(w.singleSourceWarranty)],
        ['Exclusions', w.exclusions],
        ['Maintenance Requirements', w.maintenanceRequirements],
      ]} />

      <Section title="Visual Coverage" rows={[
        ['Elevation PDF', product.visualCoverage?.elevationFileName || null],
        ['Markers', product.visualCoverage?.markers?.length ? `${product.visualCoverage.markers.length} marker(s)` : null],
      ]} />

      {/* Submittals */}
      {product.submittals && product.submittals.length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-1 mb-2 print:text-black">Submittals</h3>
          <div className="space-y-1">
            {product.submittals.map(s => (
              <div key={s.id} className="flex items-center gap-2 text-xs">
                <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                <span className="w-36 text-slate-500">{s.documentType}</span>
                <span className="flex-1 text-slate-700">{s.fileName}</span>
                <span className="text-slate-400">{s.bytes ? `${(s.bytes / 1024).toFixed(1)} KB` : 'Unknown'}</span>
                {showActions && (
                  <div className="flex items-center gap-1 print:hidden">
                    {onPreviewSubmittal && <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-400 hover:bg-orange-500 hover:text-white transition-colors" onClick={() => onPreviewSubmittal(s)}><Eye className="h-3.5 w-3.5" /></Button>}
                    {onDownloadSubmittal && <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-400 hover:bg-orange-500 hover:text-white transition-colors" onClick={() => onDownloadSubmittal(s.id, s.fileName)}><Download className="h-3.5 w-3.5" /></Button>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const QRThumbnail = ({ qrToken, size = 32 }) => {
  if (!qrToken) return <div style={{ width: size, height: size }} className="bg-slate-100 rounded" />;
  return <QRCodeSVG value={buildQrUrl(qrToken)} size={size} level="L" />;
};
